import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, User as UserIcon, Briefcase, LogOut, Plus, Pencil, Trash2, DollarSign, Camera, Upload, FileText, CheckCircle, Clock, Image, X, Home } from 'lucide-react';
import { BrickRatingDisplay } from '@/components/BrickRating';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile({ user, token, setUser }) {
  const navigate = useNavigate();
  const [workerProfile, setWorkerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWorkerForm, setShowWorkerForm] = useState(false);
  const [workerData, setWorkerData] = useState({
    specialties: [],
    hourly_rate: '',
    daily_rate: '',
    bio: '',
    experience_years: '',
    location: { address: '' }
  });
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  
  // Unit Prices State
  const [unitPrices, setUnitPrices] = useState([]);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [priceForm, setPriceForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    unit: 'servicio'
  });

  // Profile Image State
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Documents/Certificates State
  const docInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [docDescription, setDocDescription] = useState('');

  // Property Admin State
  const [propertyAdminProfile, setPropertyAdminProfile] = useState(null);
  const [buildingName, setBuildingName] = useState('');
  const [buildingAddress, setBuildingAddress] = useState('');
  const [savingBuilding, setSavingBuilding] = useState(false);
  const [paDocs, setPaDocs] = useState([]);
  const [paDocType, setPaDocType] = useState('');
  const [showPaDocDialog, setShowPaDocDialog] = useState(false);
  const [uploadingPaDoc, setUploadingPaDoc] = useState(false);
  const paDocInputRef = useRef(null);

  const propertyAdminDocTypes = {
    cedula: { label: 'Cédula de Ciudadanía', icon: FileText, color: 'blue' },
    rut_conjunto: { label: 'RUT del Conjunto/Hotel', icon: FileText, color: 'orange' }
  };

  const documentTypes = {
    cedula: { label: 'Cédula de Ciudadanía', icon: FileText, color: 'blue' },
    tarjeta_profesional: { label: 'Tarjeta Profesional', icon: FileText, color: 'purple' },
    educacion_superior: { label: 'Certificado de Educación Superior', icon: FileText, color: 'green' },
    rut: { label: 'RUT (Registro Único Tributario)', icon: FileText, color: 'orange' },
    certificado_laboral: { label: 'Certificado Laboral', icon: FileText, color: 'cyan' },
    fotos_trabajos: { label: 'Fotografías de Trabajos', icon: Image, color: 'pink' }
  };

  const specialtyOptions = [
    { value: 'plomeria', label: 'Plomería' },
    { value: 'electricidad', label: 'Electricidad' },
    { value: 'pintura', label: 'Pintura' },
    { value: 'albanileria', label: 'Albañilería' },
    { value: 'ingeniero_civil', label: 'Ingeniero Civil' },
    { value: 'arquitecto', label: 'Arquitecto' },
    { value: 'topografia', label: 'Topografía' },
    { value: 'cerrajeria', label: 'Cerrajería' },
    { value: 'planos_disenos', label: 'Planos y Diseños' },
    { value: 'licencias', label: 'Licencias de Construcción' }
  ];

  const unitOptions = [
    { value: 'servicio', label: 'Por servicio' },
    { value: 'hora', label: 'Por hora' },
    { value: 'metro', label: 'Por metro' },
    { value: 'metro2', label: 'Por m²' },
    { value: 'unidad', label: 'Por unidad' }
  ];

  useEffect(() => {
    fetchWorkerProfile();
    if (user?.role === 'property_admin') {
      fetchPropertyAdminData();
    }
  }, []);

  useEffect(() => {
    if (workerProfile) {
      fetchUnitPrices();
      fetchDocuments();
    }
  }, [workerProfile]);

  const fetchWorkerProfile = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/workers`, config);
      const myProfile = response.data.find(p => p.user_id === user.id);
      setWorkerProfile(myProfile);
      
      if (myProfile) {
        setWorkerData({
          specialties: myProfile.specialties || [],
          hourly_rate: myProfile.hourly_rate?.toString() || '',
          daily_rate: myProfile.daily_rate?.toString() || '',
          bio: myProfile.bio || '',
          experience_years: myProfile.experience_years?.toString() || '',
          location: myProfile.location || { address: '' }
        });
        setSelectedSpecialties(myProfile.specialties || []);
      }
    } catch (error) {
      console.log('No worker profile found');
    } finally {
      setLoading(false);
    }
  };

  const handleSpecialtyToggle = (value) => {
    if (selectedSpecialties.includes(value)) {
      setSelectedSpecialties(selectedSpecialties.filter(s => s !== value));
    } else {
      setSelectedSpecialties([...selectedSpecialties, value]);
    }
  };

  const handleCreateWorkerProfile = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        specialties: selectedSpecialties,
        hourly_rate: parseFloat(workerData.hourly_rate),
        daily_rate: parseFloat(workerData.daily_rate),
        bio: workerData.bio,
        experience_years: parseInt(workerData.experience_years),
        location: { address: workerData.location.address }
      };

      await axios.post(`${API}/workers/profile`, payload, config);
      toast.success('Perfil de trabajador creado exitosamente');
      setShowWorkerForm(false);
      fetchWorkerProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear perfil');
    }
  };

  // Unit Prices Functions
  const fetchUnitPrices = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/workers/my/unit-prices`, config);
      setUnitPrices(response.data);
    } catch (error) {
      console.log('Error fetching unit prices:', error);
    }
  };

  // Documents Functions
  const fetchDocuments = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/workers/my/documents`, config);
      setDocuments(response.data);
    } catch (error) {
      console.log('Error fetching documents:', error);
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedDocType) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes (JPEG, PNG, WebP) o PDF');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe superar 10MB');
      return;
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      };

      const url = `${API}/workers/documents?document_type=${selectedDocType}${docDescription ? `&description=${encodeURIComponent(docDescription)}` : ''}`;
      await axios.post(url, formData, config);
      
      toast.success('Documento subido correctamente');
      setShowDocDialog(false);
      setSelectedDocType('');
      setDocDescription('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el documento');
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) {
        docInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('¿Estás seguro de eliminar este documento?')) return;

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/workers/documents/${docId}`, config);
      toast.success('Documento eliminado');
      fetchDocuments();
    } catch (error) {
      toast.error('Error al eliminar el documento');
    }
  };

  // ===== Property Admin Functions =====
  const fetchPropertyAdminData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [profileRes, docsRes] = await Promise.all([
        axios.get(`${API}/property-admins/my/profile`, config),
        axios.get(`${API}/property-admins/my/documents`, config)
      ]);
      if (profileRes.data) {
        setPropertyAdminProfile(profileRes.data);
        setBuildingName(profileRes.data.building_name || '');
        setBuildingAddress(profileRes.data.building_address || '');
      }
      setPaDocs(docsRes.data || []);
    } catch (error) {
      console.log('Error fetching property admin data:', error);
    }
  };

  const handleSaveBuilding = async () => {
    if (!buildingName.trim() || !buildingAddress.trim()) {
      toast.error('Completa el nombre y la dirección');
      return;
    }
    setSavingBuilding(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.post(`${API}/property-admins/profile`, {
        building_name: buildingName.trim(),
        building_address: buildingAddress.trim()
      }, config);
      setPropertyAdminProfile(res.data);
      toast.success('Información del conjunto guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingBuilding(false);
    }
  };

  const handlePaDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !paDocType) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes (JPEG, PNG, WebP) o PDF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe superar 10MB');
      return;
    }

    setUploadingPaDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const config = {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      };
      const url = `${API}/property-admins/documents?document_type=${paDocType}`;
      await axios.post(url, formData, config);
      toast.success('Documento subido correctamente');
      setShowPaDocDialog(false);
      setPaDocType('');
      fetchPropertyAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el documento');
    } finally {
      setUploadingPaDoc(false);
      if (paDocInputRef.current) paDocInputRef.current.value = '';
    }
  };

  const handleDeletePaDocument = async (docId) => {
    if (!confirm('¿Estás seguro de eliminar este documento?')) return;
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/property-admins/documents/${docId}`, config);
      toast.success('Documento eliminado');
      fetchPropertyAdminData();
    } catch (error) {
      toast.error('Error al eliminar el documento');
    }
  };

  const getPaDocsByType = (type) => paDocs.filter(d => d.document_type === type);

  const getDocsByType = (type) => {
    return documents.filter(doc => doc.document_type === type);
  };

  const handleOpenPriceDialog = (price = null) => {
    if (price) {
      setEditingPrice(price);
      setPriceForm({
        name: price.name,
        description: price.description || '',
        price: price.price.toString(),
        category: price.category,
        unit: price.unit
      });
    } else {
      setEditingPrice(null);
      setPriceForm({
        name: '',
        description: '',
        price: '',
        category: workerProfile?.specialties?.[0] || '',
        unit: 'servicio'
      });
    }
    setShowPriceDialog(true);
  };

  const handleSavePrice = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = {
        name: priceForm.name,
        description: priceForm.description,
        price: parseFloat(priceForm.price),
        category: priceForm.category,
        unit: priceForm.unit
      };

      if (editingPrice) {
        await axios.put(`${API}/workers/unit-prices/${editingPrice.id}`, payload, config);
        toast.success('Precio actualizado');
      } else {
        await axios.post(`${API}/workers/unit-prices`, payload, config);
        toast.success('Precio agregado al catálogo');
      }

      setShowPriceDialog(false);
      fetchUnitPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar precio');
    }
  };

  const handleDeletePrice = async (priceId) => {
    if (!confirm('¿Estás seguro de eliminar este precio?')) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/workers/unit-prices/${priceId}`, config);
      toast.success('Precio eliminado');
      fetchUnitPrices();
    } catch (error) {
      toast.error('Error al eliminar precio');
    }
  };

  const getCategoryLabel = (value) => {
    const cat = specialtyOptions.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const getUnitLabel = (value) => {
    const unit = unitOptions.find(u => u.value === value);
    return unit ? unit.label : value;
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes JPEG, PNG o WebP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      };

      const response = await axios.post(`${API}/workers/profile-image`, formData, config);
      toast.success('Foto de perfil actualizada');
      
      // Refresh worker profile to get new image
      fetchWorkerProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Sesión cerrada');
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6">
        <div className="container mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:text-orange-500 mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-6 w-6" />
            Volver
          </button>
          <h1 className="text-2xl font-bold" data-testid="page-title">MI PERFIL</h1>
        </div>
      </div>

      {/* Profile Card */}
      <div className="container mx-auto px-4 py-6 space-y-6">
        <Card data-testid="user-info-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <Avatar className="h-20 w-20" data-testid="profile-avatar">
                <AvatarImage src="" />
                <AvatarFallback className="bg-orange-500 text-white text-2xl font-bold">
                  {user.full_name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-bold">{user.full_name}</h2>
                <p className="text-slate-500">{user.email}</p>
                <Badge className="mt-2 bg-orange-100 text-orange-700">
                  {user.role === 'client' ? 'Cliente' : user.role === 'worker' ? 'Trabajador' : user.role === 'property_admin' ? 'Administrador de Conjunto' : 'Cliente y Trabajador'}
                </Badge>
              </div>
            </div>

            <div className="space-y-3 text-sm">
              {user.phone && (
                <div>
                  <span className="font-semibold text-slate-700">Teléfono:</span>
                  <span className="ml-2 text-slate-600">{user.phone}</span>
                </div>
              )}
              {user.identification && (
                <div>
                  <span className="font-semibold text-slate-700">Identificación:</span>
                  <span className="ml-2 text-slate-600">{user.identification}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Property Admin Section */}
        {user.role === 'property_admin' && (
          <>
          <Card data-testid="property-admin-building-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5 text-orange-500" />
                Información del Conjunto/Hotel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="building-name">Nombre del Conjunto/Hotel</Label>
                  <Input
                    id="building-name"
                    data-testid="input-building-name"
                    placeholder="Ej: Conjunto Torres del Parque"
                    value={buildingName}
                    onChange={(e) => setBuildingName(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="building-address">Dirección</Label>
                  <Input
                    id="building-address"
                    data-testid="input-building-address"
                    placeholder="Ej: Cra 5 # 27-40, Bogotá"
                    value={buildingAddress}
                    onChange={(e) => setBuildingAddress(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button
                  data-testid="save-building-button"
                  onClick={handleSaveBuilding}
                  disabled={savingBuilding}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {savingBuilding ? 'Guardando...' : (propertyAdminProfile ? 'Actualizar' : 'Guardar')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="property-admin-docs-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Documentos del Administrador
                </CardTitle>
                <Badge className="bg-yellow-100 text-yellow-700">Opcional</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                Sube tu Cédula y el RUT actualizado del conjunto/hotel. Serán verificados por el equipo de Constru/clic para dar mayor confianza a los trabajadores.
              </p>
              <div className="space-y-4">
                {Object.entries(propertyAdminDocTypes).map(([type, info]) => {
                  const docs = getPaDocsByType(type);
                  const IconComp = info.icon;
                  return (
                    <div key={type} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <IconComp className="h-5 w-5 text-orange-500" />
                          <h4 className="font-semibold text-slate-700">{info.label}</h4>
                          {docs.length > 0 && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              {docs.length} archivo{docs.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`add-pa-doc-${type}`}
                          onClick={() => { setPaDocType(type); setShowPaDocDialog(true); }}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      {docs.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">No hay documentos subidos</p>
                      ) : (
                        <div className="space-y-2">
                          {docs.map(doc => (
                            <div key={doc.id} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {doc.file_url.endsWith('.pdf') ? (
                                  <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
                                ) : (
                                  <Image className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                )}
                                <p className="text-sm font-medium truncate min-w-0 flex-1">{doc.file_name}</p>
                                {doc.verified ? (
                                  <Badge className="bg-green-100 text-green-700 text-xs flex-shrink-0">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Verificado
                                  </Badge>
                                ) : (
                                  <Badge className="bg-yellow-100 text-yellow-700 text-xs flex-shrink-0">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Pendiente
                                  </Badge>
                                )}
                              </div>
                              <div className="flex gap-1 ml-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`${BACKEND_URL}${doc.file_url}?auth=${token}`, '_blank')}
                                  className="h-8 w-8 p-0"
                                >
                                  <FileText className="h-4 w-4 text-slate-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePaDocument(doc.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          </>
        )}

        {/* Worker Profile */}
        {(user.role === 'worker' || user.role === 'both') && (
          <>
          <Card data-testid="worker-profile-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Perfil de Trabajador</CardTitle>
                {!workerProfile && !showWorkerForm && (
                  <Button
                    data-testid="show-worker-form-button"
                    onClick={() => setShowWorkerForm(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    size="sm"
                  >
                    Crear Perfil
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center text-slate-500">Cargando...</p>
              ) : workerProfile ? (
                <div className="space-y-4">
                  {/* Profile Photo Section */}
                  <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                    <div className="relative">
                      <Avatar className="h-24 w-24 border-4 border-orange-200">
                        <AvatarImage 
                          src={workerProfile.profile_image ? `${BACKEND_URL}${workerProfile.profile_image}?auth=${token}` : undefined} 
                          alt="Foto de perfil"
                        />
                        <AvatarFallback className="bg-orange-100 text-orange-700 text-2xl">
                          <Briefcase className="h-10 w-10" />
                        </AvatarFallback>
                      </Avatar>
                      <button
                        data-testid="upload-photo-button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="absolute bottom-0 right-0 bg-orange-500 hover:bg-orange-600 text-white rounded-full p-2 shadow-lg transition-colors disabled:opacity-50"
                      >
                        {uploadingImage ? (
                          <Upload className="h-4 w-4 animate-pulse" />
                        ) : (
                          <Camera className="h-4 w-4" />
                        )}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        data-testid="photo-input"
                      />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-800">{user?.full_name}</h3>
                      <p className="text-sm text-slate-500">Socio Experto</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Haz clic en la cámara para cambiar tu foto
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-700 mb-2">Especialidades</h3>
                    <div className="flex flex-wrap gap-2">
                      {workerProfile.specialties?.map((specialty, idx) => (
                        <Badge key={idx} className="bg-orange-100 text-orange-700">
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-slate-700 mb-1">Tarifa/Hora</h3>
                      <p className="text-xl font-bold text-orange-600">
                        ${workerProfile.hourly_rate?.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-700 mb-1">Tarifa/Día</h3>
                      <p className="text-xl font-bold text-orange-600">
                        ${workerProfile.daily_rate?.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-700 mb-1">Experiencia</h3>
                    <p className="text-slate-600">{workerProfile.experience_years} años</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-700 mb-1">Sobre mí</h3>
                    <p className="text-slate-600">{workerProfile.bio}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-700 mb-1">Calificación</h3>
                    <div className="flex items-center gap-2">
                      <BrickRatingDisplay rating={workerProfile.rating} size="lg" />
                      <span className="text-sm text-slate-500">({workerProfile.total_jobs} trabajos)</span>
                    </div>
                  </div>
                </div>
              ) : showWorkerForm ? (
                <div className="space-y-4" data-testid="worker-form">
                  <div>
                    <Label>Especialidades</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {specialtyOptions.map(option => (
                        <Button
                          key={option.value}
                          data-testid={`specialty-${option.value}`}
                          onClick={() => handleSpecialtyToggle(option.value)}
                          variant={selectedSpecialties.includes(option.value) ? 'default' : 'outline'}
                          className={selectedSpecialties.includes(option.value) 
                            ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                            : 'border-orange-200 hover:border-orange-500'
                          }
                          size="sm"
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hourly_rate">Tarifa por Hora (COP)</Label>
                      <Input
                        id="hourly_rate"
                        data-testid="input-hourly-rate"
                        type="number"
                        placeholder="20000"
                        value={workerData.hourly_rate}
                        onChange={(e) => setWorkerData({ ...workerData, hourly_rate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="daily_rate">Tarifa por Día (COP)</Label>
                      <Input
                        id="daily_rate"
                        data-testid="input-daily-rate"
                        type="number"
                        placeholder="150000"
                        value={workerData.daily_rate}
                        onChange={(e) => setWorkerData({ ...workerData, daily_rate: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="experience_years">Años de Experiencia</Label>
                    <Input
                      id="experience_years"
                      data-testid="input-experience-years"
                      type="number"
                      placeholder="5"
                      value={workerData.experience_years}
                      onChange={(e) => setWorkerData({ ...workerData, experience_years: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="location">Ubicación</Label>
                    <Input
                      id="location"
                      data-testid="input-location"
                      placeholder="Bogotá, Colombia"
                      value={workerData.location.address}
                      onChange={(e) => setWorkerData({ 
                        ...workerData, 
                        location: { address: e.target.value } 
                      })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="bio">Sobre ti (Experiencia y habilidades)</Label>
                    <Textarea
                      id="bio"
                      data-testid="input-bio"
                      placeholder="Describe tu experiencia, habilidades y por qué deberían contratarte..."
                      value={workerData.bio}
                      onChange={(e) => setWorkerData({ ...workerData, bio: e.target.value })}
                      rows={4}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      data-testid="save-worker-profile-button"
                      onClick={handleCreateWorkerProfile}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Guardar Perfil
                    </Button>
                    <Button
                      data-testid="cancel-worker-form-button"
                      onClick={() => setShowWorkerForm(false)}
                      variant="outline"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Briefcase className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">No tienes un perfil de trabajador</p>
                  <Button
                    data-testid="create-worker-profile-button"
                    onClick={() => setShowWorkerForm(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Crear Perfil de Trabajador
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Unit Price Catalog */}
          {workerProfile && (
            <Card data-testid="unit-prices-card">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-orange-500" />
                    Catálogo de Precios
                  </CardTitle>
                  <Button
                    data-testid="add-price-button"
                    onClick={() => handleOpenPriceDialog()}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {unitPrices.length === 0 ? (
                  <div className="text-center py-8">
                    <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-2">No tienes precios en tu catálogo</p>
                    <p className="text-sm text-slate-400">
                      Agrega precios específicos por servicio para que los clientes conozcan tus tarifas
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {unitPrices.map((price) => (
                      <div
                        key={price.id}
                        data-testid={`unit-price-${price.id}`}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-800">{price.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {getCategoryLabel(price.category)}
                            </Badge>
                          </div>
                          {price.description && (
                            <p className="text-sm text-slate-500 mb-1">{price.description}</p>
                          )}
                          <p className="text-lg font-bold text-orange-600">
                            ${price.price.toLocaleString()} <span className="text-sm font-normal text-slate-500">{getUnitLabel(price.unit)}</span>
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            data-testid={`edit-price-${price.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenPriceDialog(price)}
                          >
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            data-testid={`delete-price-${price.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePrice(price.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Documents/Certificates Card */}
          {workerProfile && (
            <Card data-testid="documents-card">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-500" />
                    Documentos y Certificados
                  </CardTitle>
                  <Button
                    data-testid="add-document-button"
                    onClick={() => setShowDocDialog(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(documentTypes).map(([type, info]) => {
                    const docs = getDocsByType(type);
                    const IconComponent = info.icon;
                    
                    return (
                      <div key={type} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <IconComponent className={`h-5 w-5 text-${info.color}-500`} />
                            <h4 className="font-semibold text-slate-700">{info.label}</h4>
                            {docs.length > 0 && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                {docs.length} archivo{docs.length > 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedDocType(type);
                              setShowDocDialog(true);
                            }}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        {docs.length === 0 ? (
                          <p className="text-sm text-slate-400 italic">No hay documentos subidos</p>
                        ) : (
                          <div className="space-y-2">
                            {docs.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between bg-slate-50 p-2 rounded-lg"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  {doc.file_url.endsWith('.pdf') ? (
                                    <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
                                  ) : (
                                    <Image className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate">{doc.file_name}</p>
                                    {doc.description && (
                                      <p className="text-xs text-slate-500 truncate">{doc.description}</p>
                                    )}
                                  </div>
                                  {doc.verified ? (
                                    <Badge className="bg-green-100 text-green-700 text-xs flex-shrink-0">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Verificado
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-yellow-100 text-yellow-700 text-xs flex-shrink-0">
                                      <Clock className="h-3 w-3 mr-1" />
                                      Pendiente
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex gap-1 ml-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => window.open(`${BACKEND_URL}${doc.file_url}?auth=${token}`, '_blank')}
                                    className="h-8 w-8 p-0"
                                  >
                                    <FileText className="h-4 w-4 text-slate-500" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Nota:</strong> Los documentos serán revisados por nuestro equipo para verificar su autenticidad. 
                    Una vez verificados, aparecerán con una marca verde en tu perfil público.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          </>
        )}

        {/* Unit Price Dialog */}
        <Dialog open={showPriceDialog} onOpenChange={setShowPriceDialog}>
          <DialogContent data-testid="price-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingPrice ? 'Editar Precio' : 'Agregar Precio al Catálogo'}
              </DialogTitle>
              <DialogDescription>
                Define un precio específico para un servicio que ofreces
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="price-name">Nombre del Servicio</Label>
                <Input
                  id="price-name"
                  data-testid="price-name-input"
                  placeholder="Ej: Cambio de llave de agua"
                  value={priceForm.name}
                  onChange={(e) => setPriceForm({ ...priceForm, name: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="price-description">Descripción (opcional)</Label>
                <Textarea
                  id="price-description"
                  data-testid="price-description-input"
                  placeholder="Detalles del servicio..."
                  value={priceForm.description}
                  onChange={(e) => setPriceForm({ ...priceForm, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price-category">Categoría</Label>
                  <Select
                    value={priceForm.category}
                    onValueChange={(val) => setPriceForm({ ...priceForm, category: val })}
                  >
                    <SelectTrigger data-testid="price-category-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {workerProfile?.specialties?.map(spec => (
                        <SelectItem key={spec} value={spec}>
                          {getCategoryLabel(spec)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="price-unit">Unidad</Label>
                  <Select
                    value={priceForm.unit}
                    onValueChange={(val) => setPriceForm({ ...priceForm, unit: val })}
                  >
                    <SelectTrigger data-testid="price-unit-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {unitOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="price-amount">Precio (COP)</Label>
                <Input
                  id="price-amount"
                  data-testid="price-amount-input"
                  type="number"
                  placeholder="50000"
                  value={priceForm.price}
                  onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid="save-price-button"
                  onClick={handleSavePrice}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  disabled={!priceForm.name || !priceForm.price || !priceForm.category}
                >
                  {editingPrice ? 'Actualizar' : 'Agregar'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPriceDialog(false)}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Document Upload Dialog */}
        <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
          <DialogContent data-testid="document-dialog">
            <DialogHeader>
              <DialogTitle>Subir Documento</DialogTitle>
              <DialogDescription>
                Sube tus certificados y documentos para verificación
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tipo de Documento</Label>
                <Select
                  value={selectedDocType}
                  onValueChange={setSelectedDocType}
                >
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(documentTypes).map(([type, info]) => (
                      <SelectItem key={type} value={type}>
                        {info.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Descripción (opcional)</Label>
                <Input
                  data-testid="doc-description-input"
                  placeholder="Ej: Certificado 2024"
                  value={docDescription}
                  onChange={(e) => setDocDescription(e.target.value)}
                />
              </div>

              <div>
                <Label>Archivo</Label>
                <div className="mt-2">
                  <input
                    ref={docInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    data-testid="doc-file-input"
                  />
                  <Button
                    data-testid="select-doc-button"
                    onClick={() => docInputRef.current?.click()}
                    variant="outline"
                    className="w-full h-24 border-dashed border-2"
                    disabled={!selectedDocType || uploadingDoc}
                  >
                    {uploadingDoc ? (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-6 w-6 animate-pulse" />
                        <span>Subiendo...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-6 w-6 text-slate-400" />
                        <span className="text-slate-500">
                          {selectedDocType ? 'Haz clic para seleccionar archivo' : 'Primero selecciona el tipo'}
                        </span>
                        <span className="text-xs text-slate-400">JPG, PNG, WebP o PDF (máx. 10MB)</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => {
                  setShowDocDialog(false);
                  setSelectedDocType('');
                  setDocDescription('');
                }}
                className="w-full"
              >
                Cancelar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Property Admin Document Upload Dialog */}
        <Dialog open={showPaDocDialog} onOpenChange={setShowPaDocDialog}>
          <DialogContent data-testid="pa-doc-dialog">
            <DialogHeader>
              <DialogTitle>Subir Documento del Administrador</DialogTitle>
              <DialogDescription>
                {paDocType ? propertyAdminDocTypes[paDocType]?.label : 'Selecciona el tipo de documento'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tipo de Documento</Label>
                <Select value={paDocType} onValueChange={setPaDocType}>
                  <SelectTrigger data-testid="pa-doc-type-select">
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(propertyAdminDocTypes).map(([type, info]) => (
                      <SelectItem key={type} value={type}>{info.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Archivo</Label>
                <div className="mt-2">
                  <input
                    ref={paDocInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={handlePaDocumentUpload}
                    className="hidden"
                    data-testid="pa-doc-file-input"
                  />
                  <Button
                    data-testid="select-pa-doc-button"
                    onClick={() => paDocInputRef.current?.click()}
                    variant="outline"
                    className="w-full h-24 border-dashed border-2"
                    disabled={!paDocType || uploadingPaDoc}
                  >
                    {uploadingPaDoc ? (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-6 w-6 animate-pulse" />
                        <span>Subiendo...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-6 w-6 text-slate-400" />
                        <span className="text-slate-500">
                          {paDocType ? 'Haz clic para seleccionar archivo' : 'Primero selecciona el tipo'}
                        </span>
                        <span className="text-xs text-slate-400">JPG, PNG, WebP o PDF (máx. 10MB)</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => { setShowPaDocDialog(false); setPaDocType(''); }}
                className="w-full"
              >
                Cancelar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Logout Button */}
        <Button
          data-testid="logout-button"
          onClick={handleLogout}
          variant="outline"
          className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-500"
        >
          <LogOut className="mr-2 h-5 w-5" />
          Cerrar Sesión
        </Button>
      </div>
    </div>
  );
}